import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useKakaoSignup from "../../hooks/auth/useKakaoSignup";
import useGoogleSignup from "../../hooks/auth/useGoogleSignup";
import useKakaoLogin from "../../hooks/auth/useKakaoLogin";
import useGoogleLogin from "../../hooks/auth/useGoogleLogin";
import aivyLogo from "../../images/aivy_logo3.png";

const SocialSignup = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signupWithKakao, isLoading: isKakaoLoading, error: kakaoError } = useKakaoSignup();
  const { signupWithGoogle, isLoading: isGoogleLoading, error: googleError } = useGoogleSignup();
  const { loginWithKakao } = useKakaoLogin();
  const { loginWithGoogle } = useGoogleLogin();
  
  const [agreements, setAgreements] = useState({
    private: false,
    service: false,
    marketing: false,
  });

  const socialData = location.state as { 
    platform: "kakao" | "google";
    redirectUrl: string;
    codeVerifier?: string; 
    nonce?: string; 
  } | null;

  useEffect(() => {
    // 소셜 로그인 데이터가 없으면 로그인 페이지로 리다이렉트
    if (!socialData) {
      navigate("/auth/signin");
    }
  }, [socialData, navigate]);

  const handleAgreementChange = (type: keyof typeof agreements) => {
    setAgreements(prev => ({
      ...prev,
      [type]: !prev[type],
    }));
  };

  const handleSignup = async () => {
    if (!socialData) return;

    // 필수 동의 항목 체크
    if (!agreements.private || !agreements.service) {
      alert("필수 동의 항목에 동의해주세요.");
      return;
    }

    // 약관 동의 정보를 세션에 저장
    sessionStorage.setItem("social_agreements", JSON.stringify({
      private_agreement: agreements.private,
      service_agreeement: agreements.service,
      marketing_agreement: agreements.marketing,
    }));

    // 기존 PKCE 값들을 세션에 저장
    if (socialData.codeVerifier) {
      sessionStorage.setItem("g_code_verifier", socialData.codeVerifier);
    }
    if (socialData.nonce) {
      sessionStorage.setItem("g_nonce", socialData.nonce);
    }

    // 새로운 인증 플로우 시작 
    if (socialData.platform === "kakao") {
      loginWithKakao();
    } else if (socialData.platform === "google") {
      loginWithGoogle();
    }
  };

  const isLoading = isKakaoLoading || isGoogleLoading;
  const error = kakaoError || googleError;
  const isSignupDisabled = !agreements.private || !agreements.service || isLoading;

  if (!socialData) {
    return null; 
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white flex flex-col">
      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <div className="w-full max-w-lg">
          <div className="text-center mb-8">
            <div className="flex items-center justify-center gap-2 mb-2">
              <div className="w-14 h-14 flex items-center justify-center">
                <img
                  src={aivyLogo}
                  alt="aivy ai studio logo"
                  className="w-full h-full object-contain"
                />
              </div>
              <h1 className="h-10 text-3xl font-bold bg-gradient-to-r from-gray-800 to-gray-600 bg-clip-text text-transparent">
                Aivy AI Studio
              </h1>
            </div>
            <h2 className="text-xl font-bold text-gray-900 mb-3">
              {socialData.platform === "kakao" ? "카카오" : "구글"} 계정으로 회원가입
            </h2>
            <p className="text-gray-600 text-sm">
              {socialData.platform === "kakao" ? "카카오" : "구글"} 계정 정보를 사용하여 회원가입을 완료해주세요
            </p>
          </div>


          {error && (
            <div className="bg-red-50 text-red-600 p-3 rounded-lg text-sm text-center mb-6">
              {error}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">서비스 이용 동의</h3>
            <div className="space-y-3">
              <div className="flex flex-col border-b border-gray-200 pb-3">
                <div className="flex items-center space-x-3 mb-1">
                  <input
                    type="checkbox"
                    checked={agreements.private && agreements.service && agreements.marketing}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setAgreements({
                        private: checked,
                        service: checked,
                        marketing: checked,
                      });
                    }}
                    className="h-5 w-5 accent-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    style={{ accentColor: "#9333EA" }}
                  />
                  <label className="text-base text-gray-900 cursor-pointer">
                    약관 전체 동의
                  </label>
                </div>
                <span className="ml-8 text-xs text-gray-900">
                  선택 사항에 대한 동의를 거부하는 경우에도 서비스는 이용이
                  가능합니다.
                </span>
              </div>

              <div className="ml-6 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={agreements.private}
                      onChange={(e) => handleAgreementChange('private')}
                      className="h-4 w-4 accent-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      style={{ accentColor: "#9333EA" }}
                    />
                    <label className="text-sm text-gray-900 cursor-pointer">
                      개인정보 처리방침 동의
                      <span className="text-red-500"> (필수)</span>
                    </label>
                  </div>
                  <span className="text-sm text-gray-900 cursor-pointer">
                    <button
                      type="button"
                      className="hover:underline"
                      onClick={() => window.open("/policy/privacy", "_blank")}
                    >
                      자세히
                    </button>
                  </span>
                </div>

                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <input
                      type="checkbox"
                      checked={agreements.service}
                      onChange={(e) => handleAgreementChange('service')}
                      className="h-4 w-4 accent-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                      style={{ accentColor: "#9333EA" }}
                    />
                    <label className="text-sm text-gray-900 cursor-pointer">
                      서비스 이용약관 동의{" "}
                      <span className="text-red-500"> (필수)</span>
                    </label>
                  </div>
                  <span className="text-sm text-gray-900 cursor-pointer">
                    <button
                      type="button"
                      className="hover:underline"
                      onClick={() => window.open("/policy/terms", "_blank")}
                    >
                      자세히
                    </button>
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <input
                    type="checkbox"
                    checked={agreements.marketing}
                    onChange={(e) => handleAgreementChange('marketing')}
                    className="h-4 w-4 accent-purple-600 focus:ring-purple-500 border-gray-300 rounded"
                    style={{ accentColor: "#9333EA" }}
                  />
                  <label className="text-sm text-gray-900 cursor-pointer">
                    마케팅 정보 수신 동의
                  </label>
                </div>
              </div>
            </div>
          </div>

          <button
            onClick={handleSignup}
            disabled={isSignupDisabled}
            className={`w-full h-[45px] text-[15px] font-medium rounded-[6px] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 ${
              socialData.platform === "kakao"
                ? "bg-[#FEE500] hover:bg-[#FDD835] text-[#000000] border-[1px] border-[#F7E600]"
                : "bg-white hover:bg-gray-50 text-gray-700 border-[1px] border-gray-300"
            }`}
            style={{
              fontFamily:
                '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
            }}
          >
            {isLoading ? (
              <div className={`animate-spin rounded-full h-4 w-4 border-b-2 ${
                socialData.platform === "kakao" ? "border-black" : "border-gray-600"
              }`}></div>
            ) : (
              <>
                {socialData.platform === "kakao" ? (
                  <svg
                    className="w-[24px] h-[24px]"
                    viewBox="0 0 24 24"
                    fill="none"
                  >
                    <path
                      fillRule="evenodd"
                      clipRule="evenodd"
                      d="M12 3C7.03125 3 3 6.50781 3 10.8047C3 13.5703 4.67969 16.0078 7.125 17.4141L6.09375 21.0469C5.98438 21.4219 6.39844 21.7031 6.75 21.4688L11.25 18.7969C11.4844 18.8203 11.7422 18.8203 12 18.8203C16.9688 18.8203 21 15.3125 21 10.8047C21 6.50781 16.9688 3 12 3Z"
                      fill="#3C1E1E"
                    />
                  </svg>
                ) : (
                  <svg className="w-[20px] h-[20px]" viewBox="0 0 24 24">
                    <path
                      fill="#4285F4"
                      d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    />
                    <path
                      fill="#34A853"
                      d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                    />
                    <path
                      fill="#FBBC05"
                      d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                    />
                    <path
                      fill="#EA4335"
                      d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    />
                  </svg>
                )}
                {socialData.platform === "kakao" ? "카카오로 회원가입" : "구글로 회원가입"}
              </>
            )}
          </button>

          <div className="text-center mt-6">
            <button
              onClick={() => navigate("/auth/signin")}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              다른 방법으로 로그인하기
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SocialSignup;
